import { useState } from "react";
import { Check, ChevronRight, ChevronLeft, Building2, User, Mail, Phone, Briefcase, Users, MapPin, Globe, MessageSquare, Image, Palette, DollarSign, Send, AlertCircle } from "lucide-react";

// Email validáció
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Telefonszám formázás: XX-YYY-ZZZZ
const formatPhoneNumber = (value) => {
  // Csak számokat tartunk meg
  const numbers = value.replace(/\D/g, "");
  
  // Formázás: XX-YYY-ZZZZ
  if (numbers.length <= 2) {
    return numbers;
  } else if (numbers.length <= 5) {
    return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
  } else {
    return `${numbers.slice(0, 2)}-${numbers.slice(2, 5)}-${numbers.slice(5, 9)}`;
  }
};

// Komponensek KÍVÜL definiálva
const InputField = ({ label, icon: Icon, required, error, ...props }) => (
  <div>
    <label className="flex items-center gap-2 text-sm text-on-dark/80 mb-2">
      {Icon && <Icon size={16} className="text-primary" />}
      {label}
      {required && <span className="text-accent">*</span>}
    </label>
    <input
      className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-on-dark placeholder:text-on-dark/30 focus:ring-1 transition-all outline-none ${
        error 
          ? "border-red-500 focus:border-red-500 focus:ring-red-500/50" 
          : "border-white/10 focus:border-primary/50 focus:ring-primary/50"
      }`}
      {...props}
    />
    {error && (
      <p className="mt-1 text-sm text-red-400 flex items-center gap-1">
        <AlertCircle size={14} />
        {error}
      </p>
    )}
  </div>
);

const PhoneField = ({ label, icon: Icon, required, value, onChange, error }) => (
  <div>
    <label className="flex items-center gap-2 text-sm text-on-dark/80 mb-2">
      {Icon && <Icon size={16} className="text-primary" />}
      {label}
      {required && <span className="text-accent">*</span>}
    </label>
    <div className="flex">
      <span className="bg-white/10 border border-white/10 border-r-0 rounded-l-xl px-4 py-3 text-on-dark/70 font-medium">
        +36
      </span>
      <input
        type="tel"
        className={`flex-1 bg-white/5 border rounded-r-xl px-4 py-3 text-on-dark placeholder:text-on-dark/30 focus:ring-1 transition-all outline-none ${
          error 
            ? "border-red-500 focus:border-red-500 focus:ring-red-500/50" 
            : "border-white/10 focus:border-primary/50 focus:ring-primary/50"
        }`}
        value={value}
        onChange={(e) => onChange(formatPhoneNumber(e.target.value))}
        placeholder="30-123-4567"
        maxLength={12}
      />
    </div>
    {error && (
      <p className="mt-1 text-sm text-red-400 flex items-center gap-1">
        <AlertCircle size={14} />
        {error}
      </p>
    )}
  </div>
);

const TextAreaField = ({ label, icon: Icon, required, rows = 4, error, ...props }) => (
  <div>
    <label className="flex items-center gap-2 text-sm text-on-dark/80 mb-2">
      {Icon && <Icon size={16} className="text-primary" />}
      {label}
      {required && <span className="text-accent">*</span>}
    </label>
    <textarea
      rows={rows}
      className={`w-full bg-white/5 border rounded-xl px-4 py-3 text-on-dark placeholder:text-on-dark/30 focus:ring-1 transition-all outline-none resize-none ${
        error 
          ? "border-red-500 focus:border-red-500 focus:ring-red-500/50" 
          : "border-white/10 focus:border-primary/50 focus:ring-primary/50"
      }`}
      {...props}
    />
    {error && (
      <p className="mt-1 text-sm text-red-400 flex items-center gap-1">
        <AlertCircle size={14} />
        {error}
      </p>
    )}
  </div>
);

const RadioGroup = ({ label, icon: Icon, required, options, value, onChange, error }) => (
  <div>
    <label className="flex items-center gap-2 text-sm text-on-dark/80 mb-3">
      {Icon && <Icon size={16} className="text-primary" />}
      {label}
      {required && <span className="text-accent">*</span>}
    </label>
    <div className="space-y-2">
      {options.map((option) => (
        <div
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
            value === option.value
              ? "bg-primary/10 border-primary/40"
              : error
              ? "bg-white/5 border-red-500/50 hover:border-red-500"
              : "bg-white/5 border-white/10 hover:border-white/20"
          }`}
        >
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
            value === option.value ? "border-primary" : "border-white/30"
          }`}>
            {value === option.value && <div className="w-2.5 h-2.5 rounded-full bg-primary" />}
          </div>
          <span className="text-on-dark/90">{option.label}</span>
        </div>
      ))}
    </div>
    {error && (
      <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
        <AlertCircle size={14} />
        {error}
      </p>
    )}
  </div>
);

const CheckboxGroup = ({ label, icon: Icon, required, options, values, onToggle, error }) => (
  <div>
    <label className="flex items-center gap-2 text-sm text-on-dark/80 mb-3">
      {Icon && <Icon size={16} className="text-primary" />}
      {label}
      {required && <span className="text-accent">*</span>}
    </label>
    <div className="space-y-2">
      {options.map((option) => (
        <div
          key={option.value}
          onClick={() => onToggle(option.value)}
          className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
            values.includes(option.value)
              ? "bg-primary/10 border-primary/40"
              : error
              ? "bg-white/5 border-red-500/50 hover:border-red-500"
              : "bg-white/5 border-white/10 hover:border-white/20"
          }`}
        >
          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
            values.includes(option.value) ? "border-primary bg-primary" : "border-white/30"
          }`}>
            {values.includes(option.value) && <Check size={14} className="text-white" />}
          </div>
          <span className="text-on-dark/90">{option.label}</span>
        </div>
      ))}
    </div>
    {error && (
      <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
        <AlertCircle size={14} />
        {error}
      </p>
    )}
  </div>
);

const StepIndicator = ({ currentStep }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {[1, 2, 3, 4, 5, 6, 7].map((step) => (
      <div key={step} className="flex items-center">
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
            step === currentStep
              ? "bg-gradient-to-r from-primary to-accent text-white shadow-glow"
              : step < currentStep
              ? "bg-accent/20 text-accent"
              : "bg-white/10 text-white/40"
          }`}
        >
          {step < currentStep ? <Check size={16} /> : step}
        </div>
        {step < 7 && (
          <div className={`w-8 h-0.5 ${step < currentStep ? "bg-accent/40" : "bg-white/10"}`} />
        )}
      </div>
    ))}
  </div>
);

const AdatbekeroPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    // Step 1 - Alapadatok
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    
    // Step 2 - Szolgáltatás
    serviceDescription: "",
    
    // Step 3 - Célcsoport
    customerType: "",
    serviceArea: "",
    typicalProblems: "",
    commonObjections: "",
    
    // Step 4 - Online jelenlét
    currentOnlinePresence: "",
    contactMethods: [],
    wantsAds: "",
    
    // Step 5 - Árazás
    showPricing: "",
    pricingDetails: "",
    additionalFees: "",
    
    // Step 6 - Vizuális
    hasOwnPhotos: "",
    hasLogo: "",
    inspirationSites: "",
    brandColors: "",
    
    // Step 7 - Extra
    customerQuotes: "",
    qualifyingQuestions: ""
  });

  const totalSteps = 7;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Töröljük a hibát ha a user elkezd gépelni
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  const toggleContactMethod = (method) => {
    setFormData(prev => ({
      ...prev,
      contactMethods: prev.contactMethods.includes(method)
        ? prev.contactMethods.filter(m => m !== method)
        : [...prev.contactMethods, method]
    }));
    if (errors.contactMethods) {
      setErrors(prev => ({ ...prev, contactMethods: "" }));
    }
  };

  // Validáció lépésenként
  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1:
        if (!formData.companyName.trim()) {
          newErrors.companyName = "A vállalkozás neve kötelező";
        }
        if (!formData.contactName.trim()) {
          newErrors.contactName = "A kapcsolattartó neve kötelező";
        }
        if (!formData.email.trim()) {
          newErrors.email = "Az email cím kötelező";
        } else if (!isValidEmail(formData.email)) {
          newErrors.email = "Érvénytelen email formátum";
        }
        if (!formData.phone.trim()) {
          newErrors.phone = "A telefonszám kötelező";
        } else if (formData.phone.replace(/\D/g, "").length < 9) {
          newErrors.phone = "A telefonszám nem teljes (XX-YYY-ZZZZ)";
        }
        break;
      
      case 2:
        if (!formData.serviceDescription.trim()) {
          newErrors.serviceDescription = "A szolgáltatás leírása kötelező";
        }
        break;
      
      case 3:
        if (!formData.customerType) {
          newErrors.customerType = "Válaszd ki az ügyfélkört";
        }
        if (!formData.serviceArea.trim()) {
          newErrors.serviceArea = "A szolgáltatási terület kötelező";
        }
        break;
      
      case 4:
        if (formData.contactMethods.length === 0) {
          newErrors.contactMethods = "Válassz legalább egy kapcsolatfelvételi módot";
        }
        if (!formData.wantsAds) {
          newErrors.wantsAds = "Válaszd ki a hirdetési tervet";
        }
        break;
      
      case 5:
        if (!formData.showPricing) {
          newErrors.showPricing = "Válaszd ki az árazási módot";
        }
        if (!formData.additionalFees.trim()) {
          newErrors.additionalFees = "Ez a mező kötelező (ha nincs, írd: nincs)";
        }
        break;
      
      case 6:
        if (!formData.hasOwnPhotos) {
          newErrors.hasOwnPhotos = "Válaszd ki a képekkel kapcsolatos preferenciát";
        }
        if (!formData.hasLogo) {
          newErrors.hasLogo = "Válaszd ki a logóval kapcsolatos preferenciát";
        }
        break;
      
      case 7:
        // Nincs kötelező mező a 7. lépésben
        break;
      
      default:
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < totalSteps) setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setErrors({});
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    
    setIsSubmitting(true);
    setSubmitError("");
    
    try {
      const response = await fetch("https://landinglab-adatbekero.akcios.workers.dev", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          companyName: formData.companyName,
          contactName: formData.contactName,
          email: formData.email,
          phone: `+36 ${formData.phone}`,
          serviceDescription: formData.serviceDescription,
          customerType: formData.customerType,
          serviceArea: formData.serviceArea,
          typicalProblems: formData.typicalProblems,
          commonObjections: formData.commonObjections,
          currentOnlinePresence: formData.currentOnlinePresence,
          contactMethods: formData.contactMethods,
          wantsAds: formData.wantsAds,
          showPricing: formData.showPricing,
          pricingDetails: formData.pricingDetails,
          additionalFees: formData.additionalFees,
          hasOwnPhotos: formData.hasOwnPhotos,
          hasLogo: formData.hasLogo,
          inspirationSites: formData.inspirationSites,
          brandColors: formData.brandColors,
          customerQuotes: formData.customerQuotes,
          qualifyingQuestions: formData.qualifyingQuestions
        }),
      });
      
      if (!response.ok) {
        throw new Error("Hiba történt a küldés során");
      }
      
      setSubmitted(true);
    } catch (error) {
      setSubmitError("Hiba történt a küldés során. Kérjük, próbáld újra.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-accent" />
          </div>
          <h1 className="font-sora font-bold text-3xl text-on-dark mb-4">
            Köszönjük!
          </h1>
          <p className="text-on-dark/70 mb-8">
            Sikeresen megkaptuk az adatokat. Hamarosan jelentkezünk a következő lépésekkel kapcsolatban.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-primary to-accent text-white rounded-full px-6 py-3 font-semibold"
          >
            Vissza a főoldalra
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink">
      {/* Header */}
      <header className="py-6 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4">
          <a href="/" className="inline-block">
            <img src="/logo.svg" alt="LandingLab" className="h-8" />
          </a>
        </div>
      </header>

      <main className="py-12 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="font-sora font-bold text-2xl sm:text-3xl text-on-dark mb-3">
              Weboldal készítés adatbekérő
            </h1>
            <p className="text-on-dark/60">
              Töltsd ki az alábbi kérdéseket, hogy a lehető legjobb weboldalt készíthessük el neked.
            </p>
          </div>

          {/* Progress */}
          <StepIndicator currentStep={currentStep} />

          {/* Form Card */}
          <div className="bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-8">
            
            {/* Step 1 - Alapadatok */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Building2 size={20} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="font-sora font-semibold text-lg text-on-dark">Alapadatok</h2>
                    <p className="text-sm text-on-dark/50">Céged és elérhetőségeid</p>
                  </div>
                </div>

                <InputField
                  label="Vállalkozásod neve"
                  icon={Building2}
                  required
                  value={formData.companyName}
                  onChange={(e) => updateField("companyName", e.target.value)}
                  placeholder="Példa Kft."
                  error={errors.companyName}
                />

                <InputField
                  label="Kapcsolattartó neve"
                  icon={User}
                  required
                  value={formData.contactName}
                  onChange={(e) => updateField("contactName", e.target.value)}
                  placeholder="Kovács János"
                  error={errors.contactName}
                />

                <InputField
                  label="Email cím"
                  icon={Mail}
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  placeholder="info@example.hu"
                  error={errors.email}
                />

                <PhoneField
                  label="Telefonszám"
                  icon={Phone}
                  required
                  value={formData.phone}
                  onChange={(val) => updateField("phone", val)}
                  error={errors.phone}
                />
              </div>
            )}

            {/* Step 2 - Szolgáltatás */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Briefcase size={20} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="font-sora font-semibold text-lg text-on-dark">Szolgáltatásod</h2>
                    <p className="text-sm text-on-dark/50">Miről szól a vállalkozásod?</p>
                  </div>
                </div>

                <TextAreaField
                  label="Írd le 1-2 mondatban, mit csinálsz"
                  icon={Briefcase}
                  required
                  rows={3}
                  value={formData.serviceDescription}
                  onChange={(e) => updateField("serviceDescription", e.target.value)}
                  placeholder="Pl. Klímaszerelés és karbantartás magánszemélyeknek és cégeknek Budapest és Pest megye területén."
                  error={errors.serviceDescription}
                />

                <div className="p-4 rounded-xl bg-accent/10 border border-accent/20">
                  <p className="text-sm text-on-dark/80">
                    <strong className="text-accent">Tipp:</strong> Ha több szolgáltatásod van, sorold fel a 3 legfontosabbat prioritás sorrendben, amit a legjobban el szeretnél adni.
                  </p>
                </div>
              </div>
            )}

            {/* Step 3 - Célcsoport */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Users size={20} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="font-sora font-semibold text-lg text-on-dark">Célcsoport és piac</h2>
                    <p className="text-sm text-on-dark/50">Kik az ügyfeleid?</p>
                  </div>
                </div>

                <RadioGroup
                  label="Kik az ügyfeleid?"
                  icon={Users}
                  required
                  value={formData.customerType}
                  onChange={(val) => updateField("customerType", val)}
                  options={[
                    { value: "lakossagi", label: "Lakossági (magánszemélyek)" },
                    { value: "cegek", label: "Céges ügyfelek (B2B)" },
                    { value: "vegyes", label: "Vegyes (mindkettő)" }
                  ]}
                  error={errors.customerType}
                />

                <InputField
                  label="Milyen területen dolgozol?"
                  icon={MapPin}
                  required
                  value={formData.serviceArea}
                  onChange={(e) => updateField("serviceArea", e.target.value)}
                  placeholder="Pl. Budapest és 30 km-es körzet, vagy egész Magyarország"
                  error={errors.serviceArea}
                />

                <TextAreaField
                  label="Mi a tipikus ügyfél-probléma, amivel megkeresnek?"
                  icon={MessageSquare}
                  rows={3}
                  value={formData.typicalProblems}
                  onChange={(e) => updateField("typicalProblems", e.target.value)}
                  placeholder="Írj 2-3 példát..."
                />

                <TextAreaField
                  label="Milyen kifogások szoktak felmerülni?"
                  rows={3}
                  value={formData.commonObjections}
                  onChange={(e) => updateField("commonObjections", e.target.value)}
                  placeholder="Pl. ár, idő, bizalom, távolság..."
                />
              </div>
            )}

            {/* Step 4 - Online jelenlét */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Globe size={20} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="font-sora font-semibold text-lg text-on-dark">Online jelenlét</h2>
                    <p className="text-sm text-on-dark/50">Hol vagy már jelen online?</p>
                  </div>
                </div>

                <TextAreaField
                  label="Jelenlegi online elérhetőségeid"
                  icon={Globe}
                  rows={3}
                  value={formData.currentOnlinePresence}
                  onChange={(e) => updateField("currentOnlinePresence", e.target.value)}
                  placeholder="Pl. weboldal URL, Facebook oldal, Instagram, LinkedIn..."
                />

                <CheckboxGroup
                  label="Milyen módon szeretnéd, hogy kapcsolatba lépjenek veled? (több is választható)"
                  icon={Phone}
                  required
                  values={formData.contactMethods}
                  onToggle={toggleContactMethod}
                  options={[
                    { value: "phone", label: "Telefonszám megjelenítés és/vagy hívás gomb" },
                    { value: "email", label: "Email cím megjelenítés" },
                    { value: "form", label: "Kapcsolatfelvételi űrlap (PRO csomag része)" },
                    { value: "booking", label: "Külső időpontfoglaló rendszer (pl. Calendly)" }
                  ]}
                  error={errors.contactMethods}
                />

                <RadioGroup
                  label="Tervezel fizetett hirdetést futtatni az oldalra?"
                  required
                  value={formData.wantsAds}
                  onChange={(val) => updateField("wantsAds", val)}
                  options={[
                    { value: "yes", label: "Igen, Google / Facebook / TikTok hirdetést" },
                    { value: "no", label: "Nem, egyelőre nem tervezem" },
                    { value: "maybe", label: "Még nem tudom, később döntöm el" }
                  ]}
                  error={errors.wantsAds}
                />
              </div>
            )}

            {/* Step 5 - Árazás */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                    <DollarSign size={20} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="font-sora font-semibold text-lg text-on-dark">Árak és ajánlat</h2>
                    <p className="text-sm text-on-dark/50">Hogyan kommunikálod az áraidat?</p>
                  </div>
                </div>

                <RadioGroup
                  label="Szeretnél-e árat megjeleníteni a weboldalon?"
                  icon={DollarSign}
                  required
                  value={formData.showPricing}
                  onChange={(val) => updateField("showPricing", val)}
                  options={[
                    { value: "yes_fixed", label: "Igen, fix árakkal" },
                    { value: "yes_range", label: "Igen, 'tól-ig' árakkal" },
                    { value: "yes_packages", label: "Igen, csomagárakkal" },
                    { value: "no", label: "Nem, egyedi árazás (ajánlatkérés alapján)" }
                  ]}
                  error={errors.showPricing}
                />

                {formData.showPricing && formData.showPricing.startsWith("yes") && (
                  <TextAreaField
                    label="Írd le a konkrét árakat/csomagokat"
                    rows={4}
                    value={formData.pricingDetails}
                    onChange={(e) => updateField("pricingDetails", e.target.value)}
                    placeholder="Pl. Klíma tisztítás: 15.000 Ft, Klíma szerelés: 45.000 Ft-tól..."
                  />
                )}

                <TextAreaField
                  label="Van-e minimum díj / kiszállási díj / hétvégi felár?"
                  required
                  rows={2}
                  value={formData.additionalFees}
                  onChange={(e) => updateField("additionalFees", e.target.value)}
                  placeholder="Pl. Kiszállási díj: 3.000 Ft, Hétvégén +20%... (ha nincs, írd: nincs)"
                  error={errors.additionalFees}
                />
              </div>
            )}

            {/* Step 6 - Vizuális */}
            {currentStep === 6 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Image size={20} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="font-sora font-semibold text-lg text-on-dark">Vizuális elemek</h2>
                    <p className="text-sm text-on-dark/50">Képek, logó, design</p>
                  </div>
                </div>

                <RadioGroup
                  label="Szeretnél saját képeket megjeleníteni?"
                  icon={Image}
                  required
                  value={formData.hasOwnPhotos}
                  onChange={(val) => updateField("hasOwnPhotos", val)}
                  options={[
                    { value: "yes", label: "Igen, vannak saját fotóim" },
                    { value: "later", label: "Igen, de még készíttetem/készítem" },
                    { value: "no", label: "Nem, stock képekkel/illusztrációkkal oldjátok meg" }
                  ]}
                  error={errors.hasOwnPhotos}
                />

                <RadioGroup
                  label="Van-e már saját logód vagy márka designod?"
                  icon={Palette}
                  required
                  value={formData.hasLogo}
                  onChange={(val) => updateField("hasLogo", val)}
                  options={[
                    { value: "yes", label: "Van logóm" },
                    { value: "need", label: "Nincs, szeretném ha elkészítenétek" },
                    { value: "later", label: "Nincs, én készíttetem el máshol" }
                  ]}
                  error={errors.hasLogo}
                />

                <TextAreaField
                  label="Van az iparágadban 1-2 weboldal, aminek tetszik a designja?"
                  rows={2}
                  value={formData.inspirationSites}
                  onChange={(e) => updateField("inspirationSites", e.target.value)}
                  placeholder="URL-eket ide írhatsz... (nem másoljuk le, de segít látni a stílusodat)"
                />

                <InputField
                  label="Vannak konkrét színeid, amiket használni szeretnél?"
                  icon={Palette}
                  value={formData.brandColors}
                  onChange={(e) => updateField("brandColors", e.target.value)}
                  placeholder="Pl. kék (#1E40AF), fehér, sötétszürke..."
                />
              </div>
            )}

            {/* Step 7 - Extra információk */}
            {currentStep === 7 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                    <MessageSquare size={20} className="text-primary" />
                  </div>
                  <div>
                    <h2 className="font-sora font-semibold text-lg text-on-dark">Extra információk</h2>
                    <p className="text-sm text-on-dark/50">Segíts még jobban megérteni az ügyfeleidet</p>
                  </div>
                </div>

                <TextAreaField
                  label="Írj 3 mondatot, amit az ügyfelek gyakran mondanak, amikor érdeklődnek"
                  icon={MessageSquare}
                  rows={4}
                  value={formData.customerQuotes}
                  onChange={(e) => updateField("customerQuotes", e.target.value)}
                  placeholder="Pl. 'Sürgősen kellene...', 'Mennyibe kerül...', 'Tudnának jönni hétvégén is?'"
                />

                <TextAreaField
                  label="Írj 3 kérdést, amit te szoktál feltenni telefonon, hogy jó legyen a megrendelő"
                  rows={4}
                  value={formData.qualifyingQuestions}
                  onChange={(e) => updateField("qualifyingQuestions", e.target.value)}
                  placeholder="Pl. 'Milyen típusú klímája van?', 'Mikor lenne jó az időpont?'"
                />

                <div className="p-4 rounded-xl bg-accent/10 border border-accent/20">
                  <p className="text-sm text-on-dark/80">
                    <strong className="text-accent">Tipp:</strong> Ezek az információk segítenek abban, hogy az ügyfeleid nyelvén kommunikáljunk a weboldalon.
                  </p>
                </div>
              </div>
            )}

            {/* Submit error */}
            {submitError && (
              <div className="mt-4 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                <p className="text-sm text-red-400 flex items-center gap-2">
                  <AlertCircle size={16} />
                  {submitError}
                </p>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
              {currentStep > 1 ? (
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex items-center gap-2 px-4 py-2 text-on-dark/60 hover:text-on-dark transition-colors"
                >
                  <ChevronLeft size={18} />
                  Vissza
                </button>
              ) : (
                <div />
              )}

              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex items-center gap-2 bg-gradient-to-r from-primary to-accent text-white rounded-full px-6 py-3 font-semibold shadow-glow hover:shadow-glow-lg transition-all"
                >
                  Következő
                  <ChevronRight size={18} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex items-center gap-2 bg-gradient-to-r from-primary to-accent text-white rounded-full px-6 py-3 font-semibold shadow-glow hover:shadow-glow-lg transition-all disabled:opacity-70"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Küldés...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Beküldés
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Step titles */}
          <div className="mt-6 text-center">
            <p className="text-sm text-on-dark/40">
              {currentStep}. lépés a {totalSteps}-ből:{" "}
              <span className="text-on-dark/60">
                {currentStep === 1 && "Alapadatok"}
                {currentStep === 2 && "Szolgáltatás"}
                {currentStep === 3 && "Célcsoport"}
                {currentStep === 4 && "Online jelenlét"}
                {currentStep === 5 && "Árazás"}
                {currentStep === 6 && "Vizuális elemek"}
                {currentStep === 7 && "Extra információk"}
              </span>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdatbekeroPage;
